import React, { useEffect, useState } from 'react';
import { useContractRead } from 'wagmi';
import { CONTRACTS, VAULT_CORE_ABI, vaultConfig } from '../config/vaultConfig-v2';
import { formatUSDC, formatPercent, getHealthStatusColor, getHealthStatusText, getAllocationStatus, getAllocationColor } from '../config/vaultConfig-v2';
import './AdapterHealth.css';

function AdapterHealth() {
  const [adapters, setAdapters] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get all adapters
  const { data: adapterAddresses, isLoading: loadingAdapters } = useContractRead({
    address: CONTRACTS.VAULT_CORE,
    abi: VAULT_CORE_ABI,
    functionName: 'getAdapters',
    watch: true,
  });

  // Get total assets for allocation calculations
  const { data: totalAssets } = useContractRead({
    address: CONTRACTS.VAULT_CORE,
    abi: VAULT_CORE_ABI,
    functionName: 'totalAssets',
    watch: true,
  });

  // Fetch health and allocation data for each adapter
  useEffect(() => {
    const fetchAdapterData = async () => {
      if (!adapterAddresses || adapterAddresses.length === 0) {
        setLoading(false);
        return;
      }

      const adapterData = [];
      
      for (let i = 0; i < adapterAddresses.length; i++) {
        const address = adapterAddresses[i];
        
        // Get adapter info from config
        let adapterInfo = null;
        for (const [key, config] of Object.entries(vaultConfig.adapters)) {
          if (config.address.toLowerCase() === address.toLowerCase()) {
            adapterInfo = { name: config.name, key };
            break;
          }
        }

        adapterData.push({
          address,
          name: adapterInfo?.name || `Adapter ${i + 1}`,
          index: i,
        });
      }

      setAdapters(adapterData);
      setLoading(false);
    };

    fetchAdapterData();
  }, [adapterAddresses]);

  if (loading || loadingAdapters) {
    return (
      <div className="adapter-health">
        <h2>Adapter Health Monitor</h2>
        <div className="loading">Loading adapter status...</div>
      </div>
    );
  }

  return (
    <div className="adapter-health">
      <h2>Adapter Health Monitor</h2>
      
      <div className="health-summary">
        <div className="summary-item">
          <span className="label">Total Adapters:</span>
          <span className="value">{adapters.length}</span>
        </div>
        <div className="summary-item">
          <span className="label">Total Assets:</span>
          <span className="value">{formatUSDC(totalAssets || 0n)}</span>
        </div>
      </div>

      <div className="adapters-grid">
        {adapters.map((adapter) => (
          <AdapterCard 
            key={adapter.address} 
            adapter={adapter} 
            totalAssets={totalAssets}
          />
        ))}
      </div>
    </div>
  );
}

function AdapterCard({ adapter, totalAssets }) {
  // Get health status
  const { data: healthStatus } = useContractRead({
    address: CONTRACTS.VAULT_CORE,
    abi: VAULT_CORE_ABI,
    functionName: 'adapterHealthStatus',
    args: [adapter.address],
    watch: true,
  });

  // Get allocation
  const { data: allocation } = useContractRead({
    address: CONTRACTS.VAULT_CORE,
    abi: VAULT_CORE_ABI,
    functionName: 'adapterAllocations',
    args: [adapter.address],
    watch: true,
  });

  // Get adapter balance
  const { data: adapterBalance } = useContractRead({
    address: adapter.address,
    abi: [{ 
      name: 'totalAssets',
      type: 'function',
      stateMutability: 'view',
      inputs: [],
      outputs: [{ type: 'uint256' }]
    }],
    functionName: 'totalAssets',
    watch: true,
  });

  if (!healthStatus || !allocation) {
    return (
      <div className="adapter-card loading-card">
        <h3>{adapter.name}</h3>
        <p>Loading...</p>
      </div>
    );
  }

  const [, lastCheckTime, consecutiveFailures, isHealthy, maxDrawdownBps] = healthStatus;
  const [targetBps, minBps, maxBps] = allocation;

  // Calculate current allocation
  const currentBps = totalAssets && totalAssets > 0n && adapterBalance
    ? (adapterBalance * 10000n) / totalAssets
    : 0n;

  const allocationStatus = getAllocationStatus(Number(currentBps), Number(targetBps));
  const healthColor = getHealthStatusColor(isHealthy);
  const lastCheckDate = new Date(Number(lastCheckTime) * 1000);
  const timeSinceCheck = Date.now() - lastCheckDate.getTime();
  const hoursAgo = Math.floor(timeSinceCheck / (1000 * 60 * 60));

  return (
    <div className={`adapter-card ${!isHealthy ? 'unhealthy' : ''}`}>
      <div className="adapter-header">
        <h3>{adapter.name}</h3>
        <div className="health-indicator" style={{ backgroundColor: healthColor }}>
          {getHealthStatusText(isHealthy)}
        </div>
      </div>

      <div className="adapter-stats">
        <div className="stat">
          <span className="label">Balance:</span>
          <span className="value">{formatUSDC(adapterBalance || 0n)}</span>
        </div>

        <div className="stat">
          <span className="label">Allocation:</span>
          <div className="allocation-info">
            <span className="value" style={{ color: getAllocationColor(allocationStatus) }}>
              {formatPercent(Number(currentBps) / 100)}
            </span>
            <span className="target">/ {formatPercent(Number(targetBps) / 100)}</span>
          </div>
        </div>

        <div className="stat">
          <span className="label">Range:</span>
          <span className="value">
            {formatPercent(Number(minBps) / 100)} - {formatPercent(Number(maxBps) / 100)}
          </span>
        </div>

        <div className="stat">
          <span className="label">Max Drawdown:</span>
          <span className="value">{formatPercent(Number(maxDrawdownBps) / 100)}</span>
        </div>

        <div className="stat">
          <span className="label">Failures:</span>
          <span className={`value ${consecutiveFailures > 0 ? 'warning' : ''}`}>
            {consecutiveFailures.toString()}
          </span>
        </div>

        <div className="stat">
          <span className="label">Last Check:</span>
          <span className="value">
            {hoursAgo === 0 ? 'Recently' : `${hoursAgo}h ago`}
          </span>
        </div>
      </div>

      {!isHealthy && (
        <div className="health-warning">
          ⚠️ Adapter is currently unhealthy and not receiving deposits
        </div>
      )}

      {allocationStatus !== 'balanced' && (
        <div className="allocation-warning">
          📊 Adapter is {allocationStatus} - rebalancing may be needed
        </div>
      )}
    </div>
  );
}

export default AdapterHealth;